import React from 'react'
import { useLocation } from 'wouter'
import { Box, Typography, alpha } from '@mui/material'
import {
  Assessment,
  Build,
  Dashboard,
  Group,
  Layers,
  Notifications,
  Today,
  VpnKey,
} from '@mui/icons-material'
import AppLink from '../util/AppLink'
import { useSessionInfo } from '../util/RequireConfig'
import { serif } from './brand'
import { useBrand } from './ui'
import logo from './erecords-logo.svg'

type Item = {
  to: string
  title: string
  icon: React.ReactNode
  admin?: boolean
}

const sections: { title?: string; items: Item[] }[] = [
  {
    title: 'Monitor',
    items: [
      { to: '/overview', title: 'Overview', icon: <Dashboard /> },
      { to: '/alerts', title: 'Alerts', icon: <Notifications /> },
      { to: '/reports', title: 'Reports', icon: <Assessment /> },
    ],
  },
  {
    title: 'Setup',
    items: [
      { to: '/schedules', title: 'Schedules', icon: <Today /> },
      {
        to: '/escalation-policies',
        title: 'Escalation policies',
        icon: <Layers />,
      },
      { to: '/services', title: 'Services', icon: <VpnKey /> },
      { to: '/users', title: 'Users', icon: <Group /> },
    ],
  },
  {
    items: [{ to: '/admin', title: 'Admin', icon: <Build />, admin: true }],
  },
]

// Nav is the eRecords sidebar: "eRecords | On-Call" like Concierge and the
// sign-in pages, sections, compact single-line items.
export default function Nav(): React.JSX.Element {
  const c = useBrand()
  const [path] = useLocation()
  const { isAdmin } = useSessionInfo()

  return (
    <Box
      sx={{
        width: 232,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: c.surface,
      }}
    >
      <AppLink
        to='/overview'
        underline='none'
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 2.5,
          // same height and bottom line as the app bar next to it (66px
          // including its border), so the header reads as one strip
          height: 66,
          boxSizing: 'border-box',
          borderBottom: `1px solid ${c.line}`,
          flexShrink: 0,
        }}
        aria-label='eRecords On-Call home'
      >
        <img
          src={logo}
          alt='eRecords'
          height={19}
          style={{ display: 'block' }}
        />
        <Typography
          component='span'
          sx={{
            fontFamily: serif,
            fontSize: 17,
            lineHeight: '19px',
            color: c.ink,
            pl: 1.25,
            borderLeft: `1px solid ${alpha(c.muted, 0.45)}`,
            whiteSpace: 'nowrap',
          }}
        >
          <Box component='span' sx={{ position: 'relative', top: '2px' }}>
            On-Call
          </Box>
        </Typography>
      </AppLink>

      <Box
        component='nav'
        role='navigation'
        data-cy='nav-list'
        sx={{ px: 1.5, py: 1, flex: 1 }}
      >
        {sections.map((sec, i) => {
          const items = sec.items.filter((it) => !it.admin || isAdmin)
          if (!items.length) return null
          return (
            <Box key={i} sx={{ mb: 1.5 }}>
              {sec.title ? (
                <Typography
                  sx={{
                    px: 1.25,
                    pt: 1,
                    pb: 0.5,
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: c.muted,
                  }}
                >
                  {sec.title}
                </Typography>
              ) : (
                <Box
                  sx={{ borderTop: `1px solid ${c.line}`, mx: 1.25, my: 1 }}
                />
              )}
              {items.map((it) => {
                const active = path === it.to || path.startsWith(it.to + '/')
                return (
                  <AppLink
                    key={it.to}
                    to={it.to}
                    underline='none'
                    aria-current={active ? 'page' : undefined}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 1.25,
                      height: 38,
                      borderRadius: '10px',
                      color: active ? c.ink : c.muted,
                      fontWeight: active ? 600 : 500,
                      fontSize: 14,
                      bgcolor: active ? c.soft : 'transparent',
                      '& svg': {
                        fontSize: 20,
                        color: active ? c.ink : c.muted,
                      },
                      '&:hover': {
                        bgcolor: active ? c.soft : c.hover,
                        color: c.ink,
                        '& svg': { color: c.ink },
                      },
                    }}
                  >
                    {it.icon}
                    <span>{it.title}</span>
                  </AppLink>
                )
              })}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
